import { CSSProperties, useEffect, useRef, useState } from 'react';
import { Column } from '@tanstack/react-table';

export const leftShadowClassNames = `sticky top-0 after:pointer-events-none after:absolute after:right-[-8px] after:top-0 after:h-full after:w-2 after:shadow-[inset_10px_0_8px_-8px_rgba(0,0,0,0.06)] after:content-['']`;
export const rightShadowClassNames = `sticky top-0 after:pointer-events-none after:absolute after:left-[-8px] after:top-0 after:h-full after:w-2 after:shadow-[inset_-10px_0_8px_-8px_rgba(0,0,0,0.06)] after:content-['']`;

export const getCommonPinningStyles = <TData, TValue>(column: Column<TData, TValue>): CSSProperties => {
  const isPinned = column.getIsPinned();
  const isFirstRightPinnedColumn = isPinned === 'right' && column.getIsFirstColumn('right');
  const isLastLeftPinnedColumn = isPinned === 'left' && column.getIsLastColumn('left');

  return {
    minWidth: isPinned ? column.getSize() : undefined,
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    position: isPinned ? 'sticky' : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    zIndex: isLastLeftPinnedColumn || isFirstRightPinnedColumn ? 2 : isPinned ? 1 : undefined,
  };
};

interface UsePinnedColumnShadowOptions {
  /** Enable left pinned shadow tracking (default: true) */
  left?: boolean;
  /** Enable right pinned shadow tracking (default: true) */
  right?: boolean;
}

interface UsePinnedColumnShadowResult {
  tableRef: React.RefObject<HTMLDivElement>;
  showLeftPinnedShadow: boolean;
  showRightPinnedShadow: boolean;
}

/**
 * Tracks horizontal scroll position to show/hide shadows on pinned columns.
 */
export const usePinnedColumnShadow = ({
  left = true,
  right = true,
}: UsePinnedColumnShadowOptions = {}): UsePinnedColumnShadowResult => {
  const tableRef = useRef<HTMLDivElement>(null);
  const rafIdRef = useRef<number | null>(null);
  const [showLeftPinnedShadow, setShowLeftPinnedShadow] = useState(false);
  const [showRightPinnedShadow, setShowRightPinnedShadow] = useState(false);

  useEffect(() => {
    const tableElement = tableRef.current;

    if (!tableElement) {
      return;
    }

    const updateShadows = () => {
      const isScrolledToEnd =
        Math.abs(tableElement.scrollWidth - tableElement.clientWidth - tableElement.scrollLeft) < 1;
      const isScrolledToStart = tableElement.scrollLeft === 0;

      if (left) setShowLeftPinnedShadow(!isScrolledToStart);
      if (right) setShowRightPinnedShadow(!isScrolledToEnd);
    };

    const scheduleUpdate = () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }

      rafIdRef.current = requestAnimationFrame(() => {
        updateShadows();
        rafIdRef.current = null;
      });
    };

    scheduleUpdate();

    tableElement.addEventListener('scroll', scheduleUpdate);
    window.addEventListener('resize', scheduleUpdate);

    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }

      tableElement.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [left, right]);

  return { tableRef, showLeftPinnedShadow, showRightPinnedShadow };
};
